<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import { api, type UploadResponse } from '../lib/api';
  import { uploadSessionStore } from '../lib/stores/uploadSessionStore';
  import CheckCircle from 'lucide-svelte/icons/check-circle';
  import XCircle from 'lucide-svelte/icons/circle-x';
  import Loader from 'lucide-svelte/icons/loader';
  import ShieldCheck from 'lucide-svelte/icons/shield-check';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import { Button, Card, Spinner, StatusBadge } from '../lib/components/ui/index';
  import DocumentUpload from '../lib/components/DocumentUpload.svelte';
  import UploadDocumentTable from '../lib/components/UploadDocumentTable.svelte';
  import UploadDocumentDetail from '../lib/components/UploadDocumentDetail.svelte';

  export let batchId: string = '';

  function resetUpload() {
    uploadSessionStore.reset();
    navigate('/upload');
  }

  function handleUploaded(event: CustomEvent<UploadResponse>) {
    uploadSessionStore.startSession(event.detail.uploadBatchId, event.detail.documents);
    uploadSessionStore.startPolling();
    navigate(`/upload/${event.detail.uploadBatchId}`);
  }

  let loadingSession = false;

  onMount(async () => {
    if (batchId && !$uploadSessionStore.uploadBatchId) {
      loadingSession = true;
      try {
        await uploadSessionStore.loadSession(batchId);
      } catch {
        navigate('/upload');
        return;
      } finally {
        loadingSession = false;
      }
    }

    if (
      $uploadSessionStore.uploadBatchId &&
      !$uploadSessionStore.polling &&
      ($uploadSessionStore.status === 'pending' || $uploadSessionStore.status === 'processing')
    ) {
      uploadSessionStore.startPolling();
    }
  });

  onDestroy(() => {
    uploadSessionStore.stopPolling();
  });

  $: isComplete = $uploadSessionStore.status === 'completed';
  $: hasFailed =
    $uploadSessionStore.status === 'failed' || ($uploadSessionStore.failedDocuments ?? 0) > 0;
  $: hasUploadSession = $uploadSessionStore.uploadBatchId !== null;
  $: selectedDocument =
    $uploadSessionStore.documents.find((d) => d.id === $uploadSessionStore.selectedDocumentId) ||
    null;
  $: progressPercent =
    $uploadSessionStore.totalDocuments > 0
      ? ($uploadSessionStore.processedDocuments / $uploadSessionStore.totalDocuments) * 100
      : 0;

  let markingReviewed = false;

  async function markUploadAsReviewed() {
    markingReviewed = true;
    try {
      const docIds = $uploadSessionStore.documents
        .filter((d) => d.status !== 'failed')
        .map((d) => d.id);
      for (const id of docIds) {
        await api.documents.reviewDocument(id);
      }
      navigate('/');
    } catch (err) {
      console.error('Failed to mark uploads as reviewed:', err);
    } finally {
      markingReviewed = false;
    }
  }
</script>

<PageHeader
  title={$_('pages.upload.title')}
  breadcrumbs={[
    { label: $_('nav.dashboard'), href: '/' },
    { label: $_('pages.upload.breadcrumb') },
  ]}
/>

{#if loadingSession}
  <div class="flex justify-center py-16">
    <Spinner size="lg" label={$_('messages.loading')} />
  </div>
{:else if !hasUploadSession}
  <!-- Upload Form -->
  <DocumentUpload on:uploaded={handleUploaded} />
{:else}
  <!-- Upload Session View -->
  <Card padding="none">
    <!-- Session Header with Progress -->
    <div class="p-4 border-b border-border">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-text">{$_('upload.session.title')}</h2>
        <StatusBadge status={$uploadSessionStore.status} />
      </div>

      <!-- Progress Bar -->
      <div class="w-full h-2 bg-bg-alt rounded-full overflow-hidden mb-2">
        <div
          class="h-full rounded-full transition-all duration-300
            {isComplete ? 'bg-success' : hasFailed ? 'bg-error' : 'bg-accent'}"
          style="width: {progressPercent}%"
        ></div>
      </div>
      <p class="text-sm text-text-secondary">
        {$uploadSessionStore.processedDocuments} / {$uploadSessionStore.totalDocuments}
        {$_('upload.session.processed')}
        {#if hasFailed}
          <span class="text-error font-medium ml-1"
            >({$uploadSessionStore.failedDocuments} {$_('pages.upload.failed')})</span
          >
        {/if}
      </p>
    </div>

    <!-- Split View: Table + Detail -->
    <div class="grid grid-cols-1 lg:grid-cols-2 min-h-[400px]">
      <div class="border-r border-border overflow-y-auto">
        <UploadDocumentTable
          documents={$uploadSessionStore.documents}
          selectedDocumentId={$uploadSessionStore.selectedDocumentId}
        />
      </div>
      <div class="overflow-y-auto">
        <UploadDocumentDetail document={selectedDocument} />
      </div>
    </div>
  </Card>

  <!-- Action Buttons -->
  <div class="mt-4">
    {#if isComplete}
      <div class="flex items-center gap-3 p-4 bg-success/10 border border-success/30 rounded-lg">
        <CheckCircle class="w-5 h-5 text-success shrink-0" strokeWidth={1.5} />
        <span class="text-sm text-success flex-1">{$_('pages.upload.success')}</span>
        <div class="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            on:click={markUploadAsReviewed}
            disabled={markingReviewed}
            data-testid="upload-finalize-button"
          >
            <ShieldCheck class="w-4 h-4 mr-1.5" strokeWidth={1.5} />
            {markingReviewed ? $_('messages.saving') : $_('pages.upload.mark_reviewed')}
          </Button>
          <Button variant="secondary" size="sm" on:click={resetUpload}>
            {$_('pages.upload.upload_more')}
          </Button>
        </div>
      </div>
    {:else if hasFailed}
      <div class="flex items-center gap-3 p-4 bg-error/10 border border-error/30 rounded-lg">
        <XCircle class="w-5 h-5 text-error shrink-0" strokeWidth={1.5} />
        <span class="text-sm text-error flex-1">{$_('pages.upload.failure_message')}</span>
        <div class="flex gap-2">
          <Button variant="primary" size="sm" on:click={resetUpload}>
            {$_('pages.upload.try_again')}
          </Button>
          <Button variant="secondary" size="sm" on:click={() => navigate('/browse')}>
            {$_('pages.upload.view_successful')}
          </Button>
        </div>
      </div>
    {:else if $uploadSessionStore.polling}
      <div class="flex items-center gap-3 p-4 bg-accent-subtle border border-accent/20 rounded-lg">
        <Loader class="w-5 h-5 text-accent animate-spin shrink-0" strokeWidth={1.5} />
        <span class="text-sm text-text-secondary">{$_('pages.upload.processing')}</span>
      </div>
    {/if}
  </div>
{/if}
